import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { InstanceFooter } from '@/components/layout/InstanceFooter';
import { StackedBarChart } from '@/components/dashboard/StackedBarChart';
import { PieChart } from '@/components/dashboard/PieChart';
import { DashboardFilters, DashboardFilterState } from '@/components/dashboard/DashboardFilters';
import { RecentChanges, RecentActivityEntry } from '@/components/dashboard/RecentChanges';
import { RecentComments } from '@/components/dashboard/RecentComments';
import {
  statsApi,
  productApi,
  subProductApi,
  initiativeApi,
  statusApi,
  fiscalYearApi,
  commentApi,
  fieldConfigApi,
  labsApi,
  RecentComment,
} from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { useFieldConfig } from '@/hooks/useFieldConfig';
import { useLabsFeatures } from '@/hooks/useLabsFeatures';

export function HomePage() {
  const navigate = useNavigate();
  const {
    quarters,
    products,
    statuses,
    initiatives,
    dashboardStats,
    dashboardStatsLoading,
    refreshTrigger,
    setProducts,
    setInitiatives,
    setStatuses,
    setQuarters,
    setFieldConfigs,
    setLabsFeatures,
    setDashboardStats,
    setDashboardStatsLoading,
    openViewInitiativeModal,
  } = useAppStore();
  const fc = useFieldConfig();
  const labs = useLabsFeatures();
  const recentActivityEnabled = labs.isEnabled('recentActivity');
  const recentCommentsEnabled = labs.isEnabled('recentComments');

  const [filters, setFilters] = useState<DashboardFilterState>({});
  const [recentActivity, setRecentActivity] = useState<RecentActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [recentComments, setRecentComments] = useState<RecentComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const mountedRef = useRef(true);

  // Load reference data on mount
  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const loadReferenceData = async () => {
      try {
        const [
          productsData,
          subProductsData,
          initiativesData,
          statusesData,
          fiscalYearsData,
          fieldConfigData,
          labsData,
        ] = await Promise.all([
          productApi.getAll(),
          subProductApi.getAll(),
          initiativeApi.getAll(),
          statusApi.getAll(),
          fiscalYearApi.getAll(),
          fieldConfigApi.getAll().catch(() => []),
          labsApi.getAll().catch(() => []),
        ]);

        if (cancelled) return;

        const productsWithSubProducts = productsData.map((product: any) => ({
          ...product,
          subProducts: subProductsData.filter(
            (sp: any) => sp.productId === product.id
          ),
        }));

        setProducts(productsWithSubProducts);
        setInitiatives(initiativesData);
        setStatuses(statusesData);
        if (fieldConfigData.length > 0) {
          setFieldConfigs(fieldConfigData);
        }
        if (labsData.length > 0) {
          setLabsFeatures(labsData);
        }

        const allQuarters = fiscalYearsData
          .flatMap((fy: any) => fy.quarters || [])
          .sort((a: any, b: any) => a.displayOrder - b.displayOrder);
        setQuarters(allQuarters);
      } catch (error) {
        console.error('Failed to load reference data:', error);
      }
    };

    loadReferenceData();

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [setProducts, setInitiatives, setStatuses, setQuarters]);

  // Fetch stats - memoized function
  const fetchStats = useCallback(async (currentFilters: DashboardFilterState) => {
    setDashboardStatsLoading(true);
    try {
      const data = await statsApi.getStats(currentFilters);
      if (mountedRef.current) {
        setDashboardStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Don't set to null on error - keep previous data if available
    } finally {
      if (mountedRef.current) {
        setDashboardStatsLoading(false);
      }
    }
  }, [setDashboardStats, setDashboardStatsLoading]);

  // Fetch stats when filters change, on mount, or after changes
  useEffect(() => {
    fetchStats(filters);
  }, [filters, fetchStats, refreshTrigger]);

  // Fetch recent activity on mount and after changes (only if lab enabled)
  useEffect(() => {
    if (!recentActivityEnabled) {
      setActivityLoading(false);
      return;
    }
    let cancelled = false;

    const fetchActivity = async () => {
      setActivityLoading(true);
      try {
        const response = await statsApi.getRecentActivity(15, 0);
        if (!cancelled) {
          setRecentActivity(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch recent activity:', error);
      } finally {
        if (!cancelled) {
          setActivityLoading(false);
        }
      }
    };

    fetchActivity();

    return () => {
      cancelled = true;
    };
  }, [refreshTrigger, recentActivityEnabled]);

  // Fetch recent comments on mount and after changes (only if lab enabled)
  useEffect(() => {
    if (!recentCommentsEnabled) {
      setCommentsLoading(false);
      return;
    }
    let cancelled = false;

    const fetchComments = async () => {
      setCommentsLoading(true);
      try {
        const response = await commentApi.getRecent(9, 0);
        if (!cancelled) {
          setRecentComments(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch recent comments:', error);
      } finally {
        if (!cancelled) {
          setCommentsLoading(false);
        }
      }
    };

    fetchComments();

    return () => {
      cancelled = true;
    };
  }, [refreshTrigger, recentCommentsEnabled]);

  // Handle initiative click from recent changes
  const handleInitiativeClick = (initiativeId: string) => {
    const initiative = initiatives.find((i) => i.id === initiativeId);
    if (initiative) {
      openViewInitiativeModal(initiative);
    }
  };

  // Handle initiative click from recent comments (opens Comments tab)
  const handleCommentClick = (initiativeId: string) => {
    const initiative = initiatives.find((i) => i.id === initiativeId);
    if (initiative) {
      openViewInitiativeModal(initiative, 'comments');
    }
  };

  // Handle a stacked-bar-chart row click — go to List, pre-filtered to that product
  const handleProductClick = (productIds: string[]) => {
    navigate(`/list?product=${productIds.join(',')}`);
  };

  // Prepare chart data
  const stats = dashboardStats;
  // Show loading skeleton only on initial load when there's no data yet
  const showSkeleton = dashboardStatsLoading && !stats;

  const byProductData = stats?.byProduct || [];
  const byStatusData = stats?.byStatus?.map((s) => ({
    id: s.id,
    label: s.name,
    count: s.count,
    color: s.color,
    percentage: s.percentage,
  })) || [];
  const byClassificationData = stats?.byClassification?.map((c) => ({
    id: c.id,
    label: c.name,
    count: c.count,
    color: c.color,
    percentage: c.percentage,
  })) || [];

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <Navbar />

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          {/* Header with Filters */}
          <div className="mb-6">
            <h1 data-tour-id="dashboard-heading" className="text-2xl font-bold text-gray-900 mb-4">Dashboard</h1>
            <DashboardFilters
              quarters={quarters}
              products={products}
              statuses={statuses}
              filters={filters}
              onFiltersChange={setFilters}
            />
          </div>

          {/* Stacked Bar Chart - By Product - full width */}
          <div className="mb-4">
            {showSkeleton ? (
              <div className="bg-white rounded-lg shadow-sm p-4 min-h-[280px]">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
                  <div className="h-8 bg-gray-200 rounded w-12 animate-pulse"></div>
                </div>
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-48 h-4 bg-gray-100 rounded animate-pulse"></div>
                      <div className="flex-1 h-6 bg-gray-100 rounded animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <StackedBarChart
                title={`By ${fc.label('product')}`}
                data={byProductData}
                total={stats?.total || 0}
                onProductClick={handleProductClick}
              />
            )}
          </div>

          {/* Pie Charts - By Status / By Classification - 50/50 below the bar chart */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {showSkeleton ? (
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="h-4 bg-gray-200 rounded w-20 mb-3 animate-pulse"></div>
                <div className="flex gap-3">
                  <div className="w-36 h-36 bg-gray-100 rounded-full animate-pulse"></div>
                  <div className="flex-1 space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-3 bg-gray-100 rounded animate-pulse"></div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <PieChart title={`By ${fc.label('status')}`} data={byStatusData} />
            )}

            {fc.isVisible('classification') && (
              showSkeleton ? (
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <div className="h-4 bg-gray-200 rounded w-28 mb-3 animate-pulse"></div>
                  <div className="flex gap-3">
                    <div className="w-36 h-36 bg-gray-100 rounded-full animate-pulse"></div>
                    <div className="flex-1 space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="h-3 bg-gray-100 rounded animate-pulse"></div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <PieChart title={`By ${fc.label('classification')}`} data={byClassificationData} />
              )
            )}
          </div>

          {/* Recent Activity - Full Width */}
          {recentActivityEnabled && (
            <RecentChanges
              entries={recentActivity}
              loading={activityLoading}
              onInitiativeClick={handleInitiativeClick}
            />
          )}

          {/* Recent Comments - Full Width */}
          {recentCommentsEnabled && (
            <div className="mt-6">
              <RecentComments
                comments={recentComments}
                loading={commentsLoading}
                onInitiativeClick={handleCommentClick}
              />
            </div>
          )}
        </div>
      </main>

      <InstanceFooter />
    </div>
  );
}
